/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Organizations Service - Super Admin Organizations API
 * ============================================================================
 *
 * Service layer for organization management API calls.
 *
 * ============================================================================
 */

import { get, post, put, del } from '@/services/api/client';
import { ApiResponse, PaginatedResponse, PaginationParams } from '@/services/api/types';
import {
  Organization,
  OrganizationListItem,
  OrganizationDetail,
  OrganizationAdmin,
  OrgRoleItem,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  OrganizationFilterParams,
  CreateOrgUserRequest,
  UpdateOrgUserRequest,
  GenerateTestDataRequest,
  GenerateTestDataResponse,
  PremiumTemplate,
  AssignedPremiumTemplate,
} from '@/types/superadmin.types';

const BASE_URL = '/admin/organizations';

/**
 * Backend PagedResponse format (differs from Spring Data Page)
 */
interface BackendPagedResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Transform backend PagedResponse to frontend PaginatedResponse format (VIA_DTO)
 */
function transformPagedResponse<T>(backend: BackendPagedResponse<T>): PaginatedResponse<T> {
  return {
    content: backend.content,
    page: {
      size: backend.size,
      number: backend.page,
      totalElements: backend.totalElements,
      totalPages: backend.totalPages,
    },
  };
}

/**
 * Organizations service for Super Admin
 */
export const organizationsService = {
  /**
   * Get paginated list of organizations
   * Note: Backend returns PagedResponse directly (not wrapped in ApiResponse)
   * Note: Backend uses sortBy/sortDir instead of combined sort parameter
   */
  async getOrganizations(
    params?: PaginationParams & OrganizationFilterParams
  ): Promise<PaginatedResponse<OrganizationListItem>> {
    // Transform sort param to sortBy/sortDir for this backend endpoint
    const { sort, ...restParams } = params || {};
    let queryParams: Record<string, unknown> = { ...restParams };

    if (sort) {
      const [sortBy, sortDir] = sort.split(',');
      queryParams = { ...queryParams, sortBy, sortDir: sortDir || 'asc' };
    }

    // Backend returns PagedResponse directly, not wrapped in ApiResponse
    const response = await get<BackendPagedResponse<OrganizationListItem>>(BASE_URL, queryParams);
    return transformPagedResponse(response);
  },

  /**
   * Get organization by ID
   */
  async getOrganization(id: string): Promise<OrganizationDetail> {
    const response = await get<ApiResponse<OrganizationDetail>>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Create a new organization
   */
  async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
    const response = await post<ApiResponse<Organization>>(BASE_URL, data);
    return response.data;
  },

  /**
   * Update an organization
   */
  async updateOrganization(id: string, data: UpdateOrganizationRequest): Promise<Organization> {
    const response = await put<ApiResponse<Organization>>(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  /**
   * Suspend an organization
   */
  async suspendOrganization(id: string, reason?: string): Promise<Organization> {
    const response = await post<ApiResponse<Organization>>(`${BASE_URL}/${id}/suspend`, { reason });
    return response.data;
  },

  /**
   * Reactivate an organization
   */
  async reactivateOrganization(id: string): Promise<Organization> {
    const response = await post<ApiResponse<Organization>>(`${BASE_URL}/${id}/reactivate`);
    return response.data;
  },

  /**
   * Cancel an organization
   */
  async cancelOrganization(id: string, reason?: string): Promise<Organization> {
    const response = await post<ApiResponse<Organization>>(`${BASE_URL}/${id}/cancel`, { reason });
    return response.data;
  },

  /**
   * Get organization statistics
   */
  async getOrganizationStats(): Promise<Record<string, unknown>> {
    const response = await get<ApiResponse<Record<string, unknown>>>(`${BASE_URL}/stats`);
    return response.data;
  },

  /**
   * List available RBAC roles for an organization
   */
  async getOrgRoles(orgId: string): Promise<OrgRoleItem[]> {
    const response = await get<ApiResponse<OrgRoleItem[]>>(`${BASE_URL}/${orgId}/roles`);
    return response.data;
  },

  /**
   * Create a user for an organization
   */
  async createOrgUser(orgId: string, data: CreateOrgUserRequest): Promise<OrganizationAdmin> {
    const response = await post<ApiResponse<OrganizationAdmin>>(`${BASE_URL}/${orgId}/users`, data);
    return response.data;
  },

  /**
   * Update an organization user
   */
  async updateOrgUser(orgId: string, userId: string, data: UpdateOrgUserRequest): Promise<OrganizationAdmin> {
    const response = await put<ApiResponse<OrganizationAdmin>>(`${BASE_URL}/${orgId}/users/${userId}`, data);
    return response.data;
  },

  /**
   * Resend invitation email to an organization user
   */
  async resendOrgUserInvite(orgId: string, userId: string): Promise<void> {
    await post<ApiResponse<void>>(`${BASE_URL}/${orgId}/users/${userId}/resend-invite`);
  },

  /**
   * Manually provision a cash register for an org user who is missing one
   */
  async provisionRegister(orgId: string, userId: string): Promise<void> {
    await post<ApiResponse<void>>(`${BASE_URL}/${orgId}/users/${userId}/provision-register`);
  },

  /**
   * Generate random test data (organizations and partners)
   * Useful for testing payment flows and platform functionality.
   */
  async generateTestData(data: GenerateTestDataRequest): Promise<GenerateTestDataResponse> {
    const response = await post<ApiResponse<GenerateTestDataResponse>>(`${BASE_URL}/generate-test-data`, data);
    return response.data;
  },

  // ── Premium Invoice Templates ───────────────────────────────────────

  /**
   * Get premium templates available for assignment
   */
  async getAssignableTemplates(): Promise<PremiumTemplate[]> {
    const response = await get<ApiResponse<PremiumTemplate[]>>(`${BASE_URL}/invoice-templates/assignable`);
    return response.data;
  },

  /**
   * Get premium templates assigned to an organization
   */
  async getAssignedTemplates(orgId: string): Promise<AssignedPremiumTemplate[]> {
    const response = await get<ApiResponse<AssignedPremiumTemplate[]>>(`${BASE_URL}/${orgId}/invoice-templates`);
    return response.data;
  },

  /**
   * Assign a premium template to an organization
   */
  async assignTemplate(orgId: string, code: string): Promise<void> {
    await post<ApiResponse<void>>(`${BASE_URL}/${orgId}/invoice-templates`, { code });
  },

  /**
   * Unassign a premium template from an organization
   */
  async unassignTemplate(orgId: string, code: string): Promise<void> {
    await del<ApiResponse<void>>(`${BASE_URL}/${orgId}/invoice-templates/${code}`);
  },
};

export default organizationsService;
